import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ClipboardList, CheckCircle2, Send, Loader2, MessageSquare, CalendarClock, Download, Upload, X, Image as ImageIcon, FileText, Pencil, Mic, Video, ExternalLink } from 'lucide-react'
import { assignmentApi, type Assignment, type Submission } from '../api/lessonBuilderApi'
import { dueStatus, fmtDue, DUE_TONE_CLASS } from '../types/CourseDoc'
import { useAuth } from '../../../contexts/AuthContext'
import { openExternalClick } from '../lib/openExternal'
import KaTeXRenderer from '../../../components/common/KaTeXRenderer'
import PdfPreview from './PdfPreview'
import AnnotatedImage from './AnnotatedImage'

const isPdf = (u?: string) => !!u && /\.pdf($|\?)/i.test(u)
const isDirectVideo = (u?: string) => !!u && /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(u)

// Bài tập tự luận NHÚNG trong bài giảng: HS nộp bài (upload ảnh/PDF) / xem điểm + bản GV ghi chú; GV thấy bản xem trước + link chấm.
const AssignmentInline: React.FC<{ assignmentId?: string }> = ({ assignmentId }) => {
  const { user } = useAuth()
  const isTeacher = user?.role === 'teacher' || user?.role === 'admin'
  const [tryAsStudent, setTryAsStudent] = useState(false)   // GV muốn nộp thử → hiện form như HS
  const [a, setA] = useState<Assignment | null | undefined>(undefined)
  const [mine, setMine] = useState<Submission | null>(null)
  const [content, setContent] = useState('')
  const [files, setFiles] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    if (!assignmentId) { setA(null); return }
    assignmentApi.get(assignmentId).then((r) => {
      if (!r) { setA(null); return }
      setA(r.assignment)
      setMine(r.mySubmission)
      if (r.mySubmission) {
        setContent(r.mySubmission.content || '')
        setFiles(r.mySubmission.files && r.mySubmission.files.length ? r.mySubmission.files : (r.mySubmission.file_url ? [r.mySubmission.file_url] : []))
      }
    }).catch(() => setA(null))
  }, [assignmentId])

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = ''
    if (!f || !assignmentId) return
    setUploading(true); setMsg('')
    try {
      const r = await assignmentApi.uploadFile(assignmentId, f)
      setFiles((prev) => [...prev, r.url])
    } catch { setMsg('Tải file lên chưa thành công (chỉ nhận ảnh/PDF ≤ 25MB).') } finally { setUploading(false) }
  }

  if (!assignmentId) {
    return <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">Bài tập chưa lưu — nhập đề bài rồi bấm <strong>"Lưu bài tập"</strong> trong trình sửa thẻ để học viên nộp được.</div>
  }
  if (a === undefined) return <div className="text-sm text-gray-400">Đang tải bài tập…</div>
  if (!a) return <div className="text-sm text-gray-400">Không tải được bài tập.</div>

  const graded = mine?.status === 'graded'
  const ds = a.due_date ? dueStatus(a.due_date, { completed: !!mine }) : null

  const submit = async () => {
    if (!content.trim() && files.length === 0) { setMsg('Hãy nhập bài làm hoặc tải lên file lời giải.'); return }
    setBusy(true); setMsg('')
    try {
      const s = await assignmentApi.submit(assignmentId, content, files)
      setMine(s); setMsg('✅ Đã nộp bài. Có thể nộp lại đến khi giáo viên chấm.')
    } catch (e) {
      const m = (e as Error)?.message || ''
      setMsg(m.includes('400') ? 'Bài đã được chấm — không thể nộp lại.' : 'Nộp bài chưa thành công, thử lại.')
    } finally { setBusy(false) }
  }

  const annos = mine?.annotations || []
  const feedbackAudioUrl = mine?.feedback_audio_url || ''
  const feedbackVideoUrl = mine?.feedback_video_url || ''

  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/30 p-4">
      <div className="flex items-center gap-1.5 text-xs font-bold text-blue-600 uppercase tracking-wide mb-1">
        <ClipboardList size={15} /> Bài tập tự luận · Thang {a.max_score}đ
      </div>
      <h4 className="font-bold text-slate-900">{a.title}</h4>
      {a.attachment_url && a.attachment_type === 'image' && <img src={a.attachment_url} alt="Đề" className="w-full rounded-lg border border-gray-200 mt-2" />}
      {a.attachment_url && a.attachment_type === 'pdf' && <PdfPreview url={a.attachment_url} className="mt-2 max-h-[28rem] overflow-auto rounded-lg border border-gray-200 bg-white p-1" />}
      {a.prompt && <div className="text-sm text-slate-600 mt-1"><KaTeXRenderer content={a.prompt} /></div>}
      {a.attachment_url && <a href={a.attachment_url} onClick={openExternalClick(a.attachment_url)} className="inline-flex items-center gap-1 mt-1 text-xs font-bold text-slate-600 hover:text-slate-800"><Download size={13} /> Tải đề</a>}
      {ds && (
        <div className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold ${DUE_TONE_CLASS[ds.tone]}`}>
          <CalendarClock size={13} /> Hạn {fmtDue(a.due_date)} · {ds.label}
        </div>
      )}

      {isTeacher && !tryAsStudent ? (
        <div className="mt-3 text-sm text-slate-500 flex flex-wrap items-center gap-2">
          <span>Học viên sẽ nộp bài tại đây.</span>
          <Link to={a.course_id ? `/bai-tap?course=${a.course_id}` : '/bai-tap'} className="text-blue-600 font-bold hover:underline">Chấm bài →</Link>
          <button onClick={() => setTryAsStudent(true)}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-sky-100 text-sky-700 text-xs font-bold hover:bg-sky-200">
            <Send size={12} /> Nộp thử như học viên
          </button>
        </div>
      ) : graded ? (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="font-black text-emerald-800 flex items-center gap-1.5"><CheckCircle2 size={16} /> Điểm: {mine?.score}/{a.max_score}</div>
          {mine?.feedback && (
            <p className="text-sm text-emerald-900 mt-1 flex items-start gap-1.5">
              <MessageSquare size={14} className="mt-0.5 shrink-0" /> <span className="whitespace-pre-wrap">{mine.feedback}</span>
            </p>
          )}
          {feedbackAudioUrl && (
            <div className="mt-2">
              <div className="text-xs font-bold text-emerald-800 flex items-center gap-1"><Mic size={12} /> Audio phản hồi</div>
              <audio controls src={feedbackAudioUrl} className="mt-1 w-full" />
            </div>
          )}
          {feedbackVideoUrl && (
            <div className="mt-2">
              <div className="text-xs font-bold text-emerald-800 flex items-center gap-1"><Video size={12} /> Video phản hồi</div>
              {isDirectVideo(feedbackVideoUrl) ? (
                <video controls src={feedbackVideoUrl} className="mt-1 w-full rounded-lg border border-emerald-200 bg-black/5" />
              ) : (
                <a href={feedbackVideoUrl} onClick={openExternalClick(feedbackVideoUrl)} className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-900">
                  <ExternalLink size={12} /> Mở video phản hồi
                </a>
              )}
            </div>
          )}
          {annos.length > 0 && (
            <div className="mt-2">
              <div className="text-[11px] font-bold text-amber-700 mb-1 flex items-center gap-1"><Pencil size={12} /> Giáo viên ghi chú trên bài của bạn:</div>
              <div className="space-y-2">
                {annos.map((u, i) => (
                  <AnnotatedImage key={i} url={u} alt={`Bài đã chấm ${i + 1}`} thumbClass="w-full rounded-lg border border-amber-200" downloadName={`bai-cham-${i + 1}.png`} />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3">
          <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={5} placeholder="Nhập bài làm / ghi chú (tuỳ chọn)…"
            className="w-full p-2.5 border border-gray-200 rounded-lg text-sm leading-relaxed outline-none focus:ring-2 focus:ring-blue-200 resize-y" />
          {a.allow_file && (
            <div className="space-y-2 mt-2">
              {files.map((u, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 bg-white">
                  {isPdf(u) ? <FileText size={16} className="text-red-500 shrink-0" /> : <ImageIcon size={16} className="text-blue-500 shrink-0" />}
                  <a href={u} onClick={openExternalClick(u)} className="flex-1 min-w-0 truncate text-sm text-blue-600 hover:underline">{u.split('/').pop()}</a>
                  <button onClick={() => setFiles((p) => p.filter((_, k) => k !== i))} className="p-1 text-rose-400 hover:text-rose-600"><X size={14} /></button>
                </div>
              ))}
              <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-100 text-sky-700 text-sm font-bold hover:bg-sky-200 cursor-pointer">
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Tải lên ảnh/PDF lời giải
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onPickFile} disabled={uploading} />
              </label>
            </div>
          )}
          {msg && <p className="text-xs text-slate-600 mt-2">{msg}</p>}
          <button onClick={submit} disabled={busy || uploading}
            className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {mine ? 'Nộp lại' : 'Nộp bài'}
          </button>
        </div>
      )}
    </div>
  )
}

export default AssignmentInline
